// Encrypted request/response channel with the API (on top of HTTPS).
//
// Each tab negotiates its own AES-256-GCM key with the API (ECDH P-256 + HKDF-SHA256). The private key
// and the derived AES key are non-extractable Web Crypto keys: page scripts can use them but never read
// them, and nothing secret ships in the bundle. Request and response bodies travel as { v, iv, ct }, so
// a payload copied from DevTools, a HAR file or a proxy log is unreadable, and the method, path and
// timestamp are authenticated so a copied body can't be tampered with, moved or replayed.
//
// Plain on purpose: URL query strings and binary downloads (Excel files).
// The rendered page itself obviously shows the data to the signed-in user.

const VERSION = 1;
const { subtle } = window.crypto;
const UNDECRYPTABLE_MESSAGE = "Response could not be decrypted. Please try again.";

const toBase64 = (bytes) => {
  let binary = "";
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, view.subarray(i, i + 0x8000));
  }
  return window.btoa(binary);
};
const fromBase64 = (text) => Uint8Array.from(window.atob(text), (c) => c.charCodeAt(0));
const utf8 = (text) => new TextEncoder().encode(text);

const isEnvelope = (body) => !!body && body.v === VERSION && typeof body.iv === "string" && typeof body.ct === "string";
const isBinary = (data) => (typeof FormData !== "undefined" && data instanceof FormData)
  || (typeof Blob !== "undefined" && data instanceof Blob)
  || data instanceof ArrayBuffer
  || ArrayBuffer.isView(data);

export const isPayloadEncryptionEnabled = () => String(process.env.REACT_APP_PAYLOAD_ENCRYPTION ?? "true").toLowerCase() !== "false";

// Must match the API's normalizePath: percent-decoded, one leading slash, repeated slashes collapsed
const normalizePath = (path) => {
  let decoded = String(path || "");
  try {
    decoded = decodeURIComponent(decoded);
  } catch (err) {
    // keep as-is
  }
  return `/${decoded}`.replace(/\/{2,}/g, "/");
};

/**
 * Installs the channel on an axios instance (or the global axios) for requests to `apiUrl`.
 * @param {object} axios
 * @param {{ apiUrl: string, handshakePath: string, info: string, clientHeaders: object }} options
 */
export function installSecureChannel(axios, { apiUrl, handshakePath, info, clientHeaders }) {
  // REACT_APP_PAYLOAD_ENCRYPTION=false sends/receives plain JSON so payloads are readable in DevTools
  // while fixing errors. The API must allow it too (PAYLOAD_ENCRYPTION=false); a production API never
  // does, so a misconfigured production build fails loudly instead of going plain.
  if (!isPayloadEncryptionEnabled()) {
    // eslint-disable-next-line no-console
    console.warn("Payload encryption is OFF (REACT_APP_PAYLOAD_ENCRYPTION=false). Use for debugging only.");
    return;
  }

  // The path the API router sees: whatever follows the configured API URL. apiUrl maps to the API's root
  // (directly or through a proxy), so this holds with or without a trailing slash or path prefix.
  const apiPath = (url) => normalizePath(String(url).slice(apiUrl.length).split(/[?#]/)[0]);
  const additionalData = (direction, method, path, ts) => utf8(`${VERSION}|${direction}|${method}|${normalizePath(path)}|${ts}`);

  let pending = null;
  // Server time minus local time, from the last handshake. X-Channel-Ts must be within the API's 5-minute
  // window of its own clock, so a device whose clock is off would otherwise fail every request.
  let clockOffset = 0;
  const handshake = async () => {
    const pair = await subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"]);
    const publicKey = toBase64(await subtle.exportKey("raw", pair.publicKey));
    const { data } = await axios.post(`${apiUrl}${handshakePath}`, { publicKey }, {
      withCredentials: true, headers: clientHeaders, _skipChannel: true,
    });
    const serverKey = await subtle.importKey("raw", fromBase64(data.serverPublicKey), { name: "ECDH", namedCurve: "P-256" }, false, []);
    const bits = await subtle.deriveBits({ name: "ECDH", public: serverKey }, pair.privateKey, 256);
    const hkdf = await subtle.importKey("raw", bits, "HKDF", false, ["deriveKey"]);
    const key = await subtle.deriveKey(
      { name: "HKDF", hash: "SHA-256", salt: fromBase64(data.salt), info: utf8(info) },
      hkdf,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
    if (Number.isFinite(data.serverTime)) clockOffset = data.serverTime - Date.now();
    return { token: data.channel, key, expiresAt: data.expiresAt };
  };
  const getChannel = async () => {
    if (pending) {
      const current = await pending.catch(() => null);
      if (current && current.expiresAt - (Date.now() + clockOffset) > 60 * 1000) return current;
    }
    pending = handshake();
    pending.catch(() => { pending = null; });
    return pending;
  };
  const resetChannel = () => { pending = null; };

  const seal = async (key, value, aad) => {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const ct = await subtle.encrypt({ name: "AES-GCM", iv, additionalData: aad }, key, utf8(JSON.stringify(value)));
    return { v: VERSION, iv: toBase64(iv), ct: toBase64(ct) };
  };
  const unseal = async (key, envelope, aad) => {
    const plain = await subtle.decrypt({ name: "AES-GCM", iv: fromBase64(envelope.iv), additionalData: aad }, key, fromBase64(envelope.ct));
    return JSON.parse(new TextDecoder().decode(plain));
  };

  axios.interceptors.request.use(async (config) => {
    if (config._skipChannel || !String(config.url || "").startsWith(apiUrl)) return config;
    const channel = await getChannel();
    const method = String(config.method || "get").toUpperCase();
    const path = apiPath(config.url);
    const ts = Date.now() + clockOffset;
    config._channel = { key: channel.key, method, path, ts };
    config.headers["X-Channel"] = channel.token;
    config.headers["X-Channel-Ts"] = String(ts);
    // Informational: lets the API report a proxy that rewrites the path (it never trusts this value)
    config.headers["X-Channel-Path"] = path;

    // Keep the plain body so a retry (after refresh or a new channel) can re-encrypt it
    if (!("_plainData" in config)) config._plainData = config.data;
    const plain = config._plainData;
    if (plain !== undefined && plain !== null && !isBinary(plain)) {
      let value = plain;
      if (typeof plain === "string") {
        try {
          value = JSON.parse(plain);
        } catch (err) {
          value = plain;
        }
      }
      config.data = await seal(channel.key, value, additionalData("req", method, path, ts));
      config.headers["Content-Type"] = "application/json";
    }
    return config;
  });

  const openResponse = async (response) => {
    const ch = response?.config?._channel;
    if (!ch) return response;
    let { data } = response;
    if (typeof Blob !== "undefined" && data instanceof Blob) {
      if (!/json/i.test(data.type)) return response; // file download: plain by design
      try {
        data = JSON.parse(await data.text());
      } catch (err) {
        return response;
      }
    }
    if (isEnvelope(data)) {
      try {
        response.data = await unseal(ch.key, data, additionalData("res", ch.method, ch.path, ch.ts));
      } catch (err) {
        // Wrong or stale key: start a new channel for the next request and give callers a readable message.
        // Not retried here, since the server has already acted on the request.
        resetChannel();
        response.data = { success: false, message: UNDECRYPTABLE_MESSAGE };
        const failure = new Error(UNDECRYPTABLE_MESSAGE);
        failure.config = response.config;
        failure.request = response.request;
        failure.response = response;
        throw failure;
      }
    }
    return response;
  };

  // Registered before other response interceptors so they see decrypted bodies
  axios.interceptors.response.use(openResponse, async (error) => {
    if (error.response) {
      try {
        await openResponse(error.response);
      } catch (failure) {
        // error.response now carries the "could not be decrypted" message
      }
    }
    const code = error.response?.data?.code;
    const channelProblem = code === "channel_invalid" || code === "channel_stale";
    if (channelProblem && error.config && !error.config._channelRetried) {
      resetChannel();
      error.config._channelRetried = true;
      return axios(error.config);
    }
    throw error;
  });
}
